/* eslint-disable deprecation/deprecation */
import { Stack, styled, useThemeProps } from '@mui/material';
import { Components } from '@mui/material/styles';
import { unstable_extendSxProp as extendSxProp, SxProps } from '@mui/system';
import { getToken } from '@workiva/unify-tokens';
import clsx from 'clsx';
import * as React from 'react';
import { default as emptyViewClasses } from 'src/components/EmptyView/emptyViewClasses';
import { Typography } from 'src/components/Typography/Typography';

declare module '@mui/material/styles' {
  interface Components {
    EmptyView?: {
      defaultProps?: Partial<EmptyViewProps>;
    };
  }
}

export const emptyViewDefaultProps: Partial<Components> = {
  EmptyView: {
    defaultProps: {
      components: {
        Root: 'div',
        Icon: 'div',
        Header: 'h4',
        Content: 'div',
      },
    },
  },
};

export type EmptyViewProps = {
  /**
   * Optional Icon/Image/Symbol/Graphic at the top of the [EmptyView].
   */
  icon?: React.ReactNode;

  /**
   * Large text that conveys the purpose of the screen.
   */
  header?: string;

  /**
   * Optional body content to further assist the user.
   */
  children?: React.ReactNode;

  /**
   * Class name applied to the root element.
   */
  className?: string;

  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;

  /**
   * The components used for each slot inside the InputBase.
   * Either a string to use a HTML element or a component.
   * @default {}
   */
  components?: {
    Root?: React.ElementType;
    Icon?: React.ElementType;
    Header?: React.ElementType;
    Content?: React.ElementType;
  };
};

/**
 * The EmptyView component is used to provide
 * messaging to users about an empty set of results
 * or an empty view such as a 404 / 500 error page.
 *
 * Example:
 * ```dart
 * (EmptyView()
 *   ..icon = (Icon()..glyph = IconGlyph.TWFR_HOURGLASS)()
 *   ..header = 'Loading'
 * )(
 *   Dom.p()('Please wait while the desired action is being performed.'),
 * )
 * ```
 */
export const EmptyView = React.forwardRef<HTMLDivElement, EmptyViewProps>(
  function EmptyView(inProps, ref) {
    const themeProps = useThemeProps({ props: inProps, name: 'EmptyView' });
    const props = extendSxProp(themeProps);
    const {
      className,
      icon: iconProp,
      header: headerProp,
      children: childrenProp,
      components,
      ...other
    } = props;

    const rootClasses = clsx([emptyViewClasses.root, className]);

    const icon = iconProp && (
      <EmptyViewIcon
        className={emptyViewClasses.icon}
        {...(components?.Icon !== 'div'
          ? {
              as: components?.Icon,
            }
          : {})}
      >
        {iconProp}
      </EmptyViewIcon>
    );

    const header = headerProp && (
      <EmptyViewHeader
        variant="h4"
        {...(components?.Header !== 'h4'
          ? {
              component: components?.Header,
            }
          : {})}
        className={emptyViewClasses.header}
      >
        {headerProp}
      </EmptyViewHeader>
    );

    const children = childrenProp && (
      <EmptyViewContent
        {...(components?.Content !== 'div'
          ? {
              as: components?.Content,
            }
          : {})}
        className={emptyViewClasses.content}
      >
        {childrenProp}
      </EmptyViewContent>
    );

    return (
      <EmptyViewRoot
        spacing={2}
        role="status"
        ref={ref}
        className={rootClasses}
        // Conditionally attach `as` because the root is set by default to
        // be `Stack`. Passing this through unconditionally will break
        // conformance tests.
        {...(components?.Root !== Stack
          ? {
              as: components?.Root,
            }
          : {})}
        {...other}
      >
        {icon}
        {header}
        {children}
      </EmptyViewRoot>
    );
  }
);

const EmptyViewRoot = styled(Stack, {
  name: 'EmptyView',
  slot: 'Root',
  shouldForwardProp: (prop) => prop !== 'components',
})({
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  flex: '1 1 0%',
  color: getToken('Core/Text/text-default'),
});

const emptyViewIconSizeInPx = 48;
const EmptyViewIcon = styled('div', {
  name: 'EmptyView',
  slot: 'Icon',
})(
  // This component has styles for both <svg> and <i>
  // in order to support dual compat with WSD + MUI + Unify icons.
  {
    display: 'inline-block',
    width: emptyViewIconSizeInPx,
    height: emptyViewIconSizeInPx,
    color: getToken('Core/Icon/icon-default'),
    margin: '0 auto',
    '& > *, & .icon': {
      fontSize: emptyViewIconSizeInPx,
      width: emptyViewIconSizeInPx,
      height: emptyViewIconSizeInPx,
      color: getToken('Core/Icon/icon-default'),
      fill: getToken('Core/Icon/icon-default'),
    },
    '& > .MuiIcon-root': {
      fontSize: emptyViewIconSizeInPx,
      width: 48,
      height: 48,
    },
  }
);

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const EmptyViewHeader = styled(Typography, {
  name: 'EmptyView',
  slot: 'Header',
})(({ theme }) => {
  return {
    fontWeight: theme.typography.fontWeightBold,
    color: getToken('Core/Text/text-default'),
  };
}) as typeof Typography;

const EmptyViewContent = styled('div', {
  name: 'EmptyView',
  slot: 'Content',
})(({ theme }) => {
  return {
    fontWeight: theme.typography.fontWeightRegular,
    color: getToken('Core/Text/text-default'),
    lineHeight: '170%',
  };
});

export default EmptyView;
